import classnames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { debounce, forEach, get, isArray, isEmpty } from 'lodash';
import { useCallback, useEffect, useReducer } from 'react';
import { withTranslation } from 'react-i18next';

import { Error } from '@krea/common/shared-components/fields/error';
import Fieldset from '@krea/common/shared-components/fields/fieldset';
import { FileInput } from '@krea/common/shared-components/fields/file/index';
import FileItem from '@krea/common/shared-components/fields/file-item';
import FilesList from '@krea/common/shared-components/fields/files-list';
import { Label } from '@krea/common/shared-components/fields/label';
import {
  useDeleteFile,
  usePostFieldRequest,
} from '@krea/common/store/field-requests/hooks/mutations';
import { useGetOneFieldRequest } from '@krea/common/store/field-requests/hooks/queries';

import FieldRequestPreviewHeader from '../FieldRequestPreviewHeader';
import FieldRequestSaved from '../FieldRequestSaved';

const UnpaidInvoicesAdditionalDataForm = ({
  fieldRequestID,
  isPreviewMode,
  t,
}) => {
  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);
  const { mutate: deleteFile } = useDeleteFile();

  const errors = error?.data?.errors || [];

  const { files = [] } = submittedData;

  const deleteFileFunc = (fileID) => {
    deleteFile({ fileID, fieldRequestID });
  };

  function reducer(state, action) {
    switch (action.type) {
      case 'touched':
        return { ...state, touched: action.payload };

      default:
        return { ...state };
    }
  }

  const [state, setState] = useReducer(reducer, {
    touched: false,
  });

  const createFormData = (value) => {
    const formData = new FormData();

    if (isArray(value.files) && !isEmpty(value.files)) {
      forEach(value.files, (file) => {
        formData.append('files', file, file.name);
      });
    }

    return formData;
  };

  const onBlur = (state, files = null) => {
    const { touched } = state;
    const value = state;

    value.files = isArray(files) ? files : null;
    const formData = createFormData(value);

    if (touched || isArray(files)) {
      postFieldRequest(formData);

      if (touched) {
        setState({ type: 'touched', payload: false });
      }
    }
  };

  const debouncedFunc = debounce((state) => onBlur(state), 2500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback((state) => debouncedFunc(state), []);

  useEffect(() => {
    debouncedSave(state);

    return () => {
      debouncedFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const formClasses = classnames('input-form', {
    'input-form-padded': isPreviewMode,
  });

  return (
    <div className={formClasses}>
      {isPreviewMode ? (
        <>
          {files.length > 0 && (
            <FieldRequestPreviewHeader
              label={displayName}
              value={
                <>
                  <AnimatePresence>
                    {files.map((file, index) => (
                      <FileItem
                        key={index}
                        file={file}
                        deleteFile={deleteFileFunc}
                        isPreviewMode={isPreviewMode}
                      />
                    ))}
                  </AnimatePresence>
                </>
              }
            />
          )}
        </>
      ) : (
        <>
          <Fieldset>
            <div className="mb-5">
              <Label className="mb-2">
                {t('iaq.unpaidInvoices.accountsLedger.question')}
              </Label>

              <FileInput
                multiple
                id="InvoicesFileInput"
                files={files}
                postFiles={(e) => onBlur(state, e)}
                error={get(errors, '[0].message', null)}
              />

              {files.length > 0 && (
                <FilesList label={t('iaq.uploadedFiles')} className="mt-5">
                  <AnimatePresence>
                    {files.map((file, index) => {
                      return (
                        <FileItem
                          key={index}
                          file={file}
                          deleteFile={deleteFileFunc}
                          isPreviewMode={isPreviewMode}
                        />
                      );
                    })}
                  </AnimatePresence>
                </FilesList>
              )}
            </div>
          </Fieldset>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />

          {errors.map((error, index) => (
            <Error className="mt-3" key={index}>
              {error.message}
            </Error>
          ))}
        </>
      )}
    </div>
  );
};

export default withTranslation()(UnpaidInvoicesAdditionalDataForm);
